import { Icon } from '@iconify/react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import moreIcon from '@iconify/icons-ant-design/more-outlined';
import { useNavigate } from 'react-router-dom';
import useMutation, { METHOD } from 'hook/useMutation';
import { API_PATH } from 'common/constants/services';
import { toast } from 'react-toastify';
import { addComma } from 'utils/comma';
import { Td, Tr } from 'components/Table';
import {
  ChallengeListWrapper,
  ChallengeItem,
  ChallengeMenuWrapper,
  ChallengeClickArea,
} from './style';

interface IProps {
  rows: IChallenge[];
  refetchQuery: () => void;
}

interface IMenuProps {
  data: IChallenge;
  refetchQuery: () => void;
}

function ChallengeMenu({ data, refetchQuery }: IMenuProps) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openConfirm, toggleConfirm] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const deleteChallenge = useMutation<number>(API_PATH.CHALLENGES, {
    method: METHOD.DELETE,
    onSuccess: () => {
      handleClose();
      toggleConfirm(false);
      toast.success('삭제가 완료되었습니다.');
      refetchQuery();
    },
  });

  const updateChallenge = useMutation<IChallengePatchActivate>(
    `${API_PATH.CHALLENGES}${data.id}/`,
    {
      method: METHOD.PATCH,
      onSuccess: () => {
        handleClose();
        toast.success('공개여부가 변경되었습니다.');
        refetchQuery();
      },
    },
  );

  const onClickModify = () => {
    navigate(`/challenges/modify/${data.id}/info`);
  };

  const onClickDeleteMenu = () => {
    toggleConfirm(true);
  };

  const onClickDelete = () => {
    deleteChallenge.mutate(data.id);
  };

  const onClickChangePrivate = (changeType: boolean) => {
    updateChallenge.mutate({
      activated: changeType,
    });
  };

  const onClickNavigateCondition = () => {
    navigate(`/challenges/modify/${data.id}/conditions`);
  };

  const onClickNavigateRoutine = () => {
    navigate(`/challenges/modify/${data.id}/routines`);
  };

  return (
    <>
      <Box>
        <IconButton onClick={handleClick}>
          <Icon icon={moreIcon} />
        </IconButton>
        <ChallengeMenuWrapper
          id='basic-menu'
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          <MenuItem onClick={onClickModify}>수정</MenuItem>
          <MenuItem onClick={onClickDeleteMenu}>삭제</MenuItem>
          <MenuItem onClick={() => onClickChangePrivate(true)}>
            공개로 전환
          </MenuItem>
          <MenuItem onClick={() => onClickChangePrivate(false)}>
            비공개로 전환
          </MenuItem>
          <MenuItem onClick={onClickNavigateCondition}>컨디션</MenuItem>
          <MenuItem onClick={onClickNavigateRoutine}>루틴</MenuItem>
        </ChallengeMenuWrapper>
      </Box>

      <Dialog
        open={openConfirm}
        onClose={() => toggleConfirm(false)}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            삭제하시겠습니까?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => toggleConfirm(false)}>취소</Button>
          <Button onClick={onClickDelete} color='error' autoFocus>
            삭제
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export function ChallengeList({ rows, refetchQuery }: IProps) {
  const navigate = useNavigate();

  const onClickListItem = (id: number) => {
    navigate(`/challenges/modify/${id}/info`);
  };

  return (
    <>
      {rows.map((row: IChallenge) => (
        <Tr key={row.id}>
          <ChallengeClickArea onClick={() => onClickListItem(row.id)}>
            <Td>
              <ChallengeItem
                sx={{
                  width: 400,
                }}
              >
                <Typography sx={{ paddingLeft: 1, fontWeight: 'bold' }}>
                  {row.name}
                </Typography>
              </ChallengeItem>
            </Td>
            <Td width='100px'>
              <ChallengeItem
                sx={{
                  width: 100,
                  justifyContent: 'center',
                }}
              >
                <Chip
                  label={row.activated ? '전체공개' : '비공개'}
                  color={row.activated ? 'secondary' : 'error'}
                  size='small'
                />
              </ChallengeItem>
            </Td>
            <Td width='100px'>
              <ChallengeItem
                sx={{
                  width: 100,
                  justifyContent: 'center',
                }}
              >
                <Chip
                  label={row.is_pro ? 'Pro루틴' : '무료루틴'}
                  color={row.is_pro ? 'secondary' : 'error'}
                  size='small'
                />
              </ChallengeItem>
            </Td>
            <Td width='200px'>
              <ChallengeItem
                sx={{
                  width: 200,
                  justifyContent: 'center',
                }}
              >
                <Typography
                  align='center'
                  sx={{ fontSize: 11, fontWeight: 'bold' }}
                >
                  {row.tags.reduce((a, c) => addComma(a, c.name), '')}
                </Typography>
              </ChallengeItem>
            </Td>
            <Td width='100px'>
              <ChallengeItem
                sx={{
                  width: 100,
                  justifyContent: 'center',
                }}
              >
                <Typography align='center'>{row.total_date}</Typography>
              </ChallengeItem>
            </Td>
            <Td width='50px'>
              <ChallengeItem
                sx={{
                  width: 24,
                  justifyContent: 'center',
                }}
              >
                <ChallengeMenu data={row} refetchQuery={refetchQuery} />
              </ChallengeItem>
            </Td>
          </ChallengeClickArea>
        </Tr>
      ))}
    </>
  );
}
